import { TIKTOK_CLIENT_KEY, TIKTOK_REDIRECT_URI, TIKTOK_SCOPES } from '../config/tiktok';
import { supabase } from './supabaseClient';

class TikTokAuth {
  constructor() {
    this.clientKey = TIKTOK_CLIENT_KEY;
    this.redirectUri = TIKTOK_REDIRECT_URI;
    this.scopes = TIKTOK_SCOPES;
    this.storageKey = 'tiktok_auth';
  }

  // Generate CSRF token for security
  generateCSRFToken() {
    const token = 'tiktok_oauth_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem('tiktok_csrf_token', token);
    return token;
  }

  // Verify CSRF token
  verifyCSRFToken(token) {
    const storedToken = sessionStorage.getItem('tiktok_csrf_token');
    // Don't remove the token yet - keep it for debugging if needed
    const isValid = token === storedToken;
    if (isValid) {
      sessionStorage.removeItem('tiktok_csrf_token');
    }
    return isValid;
  }

  // Initiate TikTok OAuth flow
  signInWithTikTok() {
    const csrfState = this.generateCSRFToken();
    
    const authUrl = new URL('https://www.tiktok.com/v2/auth/authorize/');
    authUrl.searchParams.append('client_key', this.clientKey);
    authUrl.searchParams.append('scope', this.scopes);
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('redirect_uri', this.redirectUri);
    authUrl.searchParams.append('state', csrfState);

    console.log('TikTok OAuth URL:', authUrl.toString());
    window.location.href = authUrl.toString();
  }

  // Handle OAuth callback
  async handleTikTokCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const scopes = urlParams.get('scopes');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    // Debug logging for callback parameters
    console.log('TikTok callback check - URL params:', { code: !!code, state, scopes, error, errorDescription });

    // Check if this is a TikTok OAuth callback using state prefix
    const savedState = sessionStorage.getItem('tiktok_csrf_token');
    if (!state || !savedState || !state.startsWith('tiktok_oauth_')) {
      // Not a TikTok OAuth callback, ignore silently
      return null;
    }

    // Handle OAuth errors from TikTok
    if (error) {
      console.error('TikTok OAuth error:', error, errorDescription);
      sessionStorage.removeItem('tiktok_csrf_token');
      return { error: errorDescription || error };
    }

    // Need code to proceed
    if (!code) {
      console.error('TikTok callback missing authorization code');
      return { error: 'Missing authorization code' };
    }

    console.log('TikTok callback detected with code:', code);

    // Verify CSRF token
    if (!this.verifyCSRFToken(state)) {
      console.error('CSRF token mismatch - stored:', savedState, 'received:', state);
      return { error: 'Security verification failed. Please try again.' };
    }

    try {
      // Exchange code for access token via backend
      const response = await fetch('https://toldfel.hu/tiktok/exchange', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      const data = await response.json();
      console.log('TikTok token exchange response:', data);

      if (!response.ok) {
        const errorMsg = data.error || 'Failed to exchange code for token';
        console.error('TikTok exchange error details:', data);
        throw new Error(errorMsg);
      }

      if (data.access_token) {
        const tokenData = {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_in: data.expires_in,
          open_id: data.open_id,
          scope: data.scope,
          token_type: data.token_type,
          expiresAt: new Date(Date.now() + data.expires_in * 1000).toISOString()
        };

        // Store in localStorage for immediate access
        localStorage.setItem(this.storageKey, JSON.stringify(tokenData));

        // Store in Supabase for persistence
        const session = supabase.getSession();
        if (session && session.user && session.user.id) {
          await supabase.upsertPlatformToken({
            user_id: session.user.id,
            platform: 'tiktok',
            access_token: data.access_token,
            refresh_token: data.refresh_token,
            expires_at: tokenData.expiresAt,
            meta: {
              open_id: data.open_id,
              scope: data.scope,
              token_type: data.token_type
            }
          });
        }

        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        
        console.log('TikTok authentication successful');
        return { success: true, token: tokenData };
      }
      return { error: 'No access token received' };
    } catch (error) {
      console.error('TikTok authentication error:', error);
      return { error: error.message || 'Failed to connect to TikTok' };
    }
  }

  // Get stored token
  getToken() {
    const stored = localStorage.getItem(this.storageKey);
    if (!stored) return null;

    try {
      const token = JSON.parse(stored);
      
      // Check if token is expired - mark it but don't delete
      // The getValidAccessToken method will handle refreshing
      if (token.expiresAt && new Date(token.expiresAt) <= new Date()) {
        console.log('TikTok token expired');
        return { ...token, expired: true };
      }

      return token;
    } catch (error) {
      console.error('Error parsing TikTok token:', error);
      return null;
    }
  }

  // Refresh the access token using the refresh token
  async refreshAccessToken() {
    const token = this.getToken();
    if (!token?.refresh_token) {
      console.error('No refresh token available for TikTok');
      return null;
    }

    try {
      const session = supabase.getSession();
      const userId = session?.user?.id || null;

      const response = await fetch('https://toldfel.hu/tiktok/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: token.refresh_token,
          user_id: userId
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Token refresh failed');
      }

      const data = await response.json();
      console.log('TikTok token refreshed successfully');

      // Update stored token
      const updatedToken = {
        ...token,
        access_token: data.access_token,
        refresh_token: data.refresh_token || token.refresh_token,
        expiresAt: data.expires_at,
        expires_in: data.expires_in,
        expired: false
      };

      localStorage.setItem(this.storageKey, JSON.stringify(updatedToken));

      // Update in Supabase
      if (userId) {
        try {
          await supabase.upsertPlatformToken({
            user_id: userId,
            platform: 'tiktok',
            access_token: updatedToken.access_token,
            refresh_token: updatedToken.refresh_token,
            expires_at: updatedToken.expiresAt,
            meta: {
              open_id: updatedToken.open_id,
              scope: updatedToken.scope,
              token_type: updatedToken.token_type
            }
          });
        } catch (e) {
          console.error('Failed to update TikTok token in Supabase:', e);
        }
      }

      return updatedToken;
    } catch (error) {
      console.error('Failed to refresh TikTok token:', error);
      // Don't auto-delete tokens on refresh failure
      // Let the user manually reconnect
      return null;
    }
  }

  // Check if we have a token (doesn't validate if it works)
  hasToken() {
    const token = this.getToken();
    return !!(token && token.access_token);
  }

  // Get a valid access token, refreshing if necessary
  async getValidAccessToken(forceRefresh = false) {
    let token = this.getToken();
    
    if (!token?.access_token) {
      return null;
    }

    // If force refresh is requested or token is expired or about to expire (within 5 minutes), refresh it
    const shouldRefresh = forceRefresh || (token.expiresAt && token.refresh_token && (() => {
      const expiresAt = new Date(token.expiresAt);
      const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000);
      return expiresAt < fiveMinutesFromNow;
    })());

    if (shouldRefresh) {
      console.log('TikTok token expired or expiring soon, refreshing...');
      const refreshed = await this.refreshAccessToken();
      if (!refreshed) {
        // Refresh failed, but don't delete the token
        // Return null to indicate we can't get a valid token right now
        return null;
      }
      token = refreshed;
    }

    return token?.access_token || null;
  }

  // Load token from Supabase
  async loadTokenFromSupabase() {
    const session = supabase.getSession();
    if (!session || !session.user || !session.user.id) {
      return null;
    }

    try {
      const tokens = await supabase.fetchPlatformTokens(session.user.id);
      const tiktokToken = tokens.find(t => t.platform === 'tiktok');

      if (tiktokToken && tiktokToken.access_token) {
        const tokenData = {
          access_token: tiktokToken.access_token,
          refresh_token: tiktokToken.refresh_token,
          open_id: tiktokToken.meta?.open_id,
          scope: tiktokToken.meta?.scope,
          token_type: tiktokToken.meta?.token_type || 'Bearer',
          expiresAt: tiktokToken.expires_at
        };

        localStorage.setItem(this.storageKey, JSON.stringify(tokenData));
        return tokenData;
      }
    } catch (error) {
      console.error('Failed to load TikTok token from Supabase:', error);
    }

    return null;
  }

  // Sign out
  async signOut() {
    localStorage.removeItem(this.storageKey);
    
    // Remove from Supabase
    try {
      const session = supabase.getSession();
      if (session && session.user && session.user.id) {
        await supabase.deletePlatformToken(session.user.id, 'tiktok');
        console.log('TikTok token deleted from Supabase');
      }
    } catch (e) {
      console.error('Failed to delete TikTok token from Supabase:', e);
    }
  }
}

const tiktokAuth = new TikTokAuth();

export default tiktokAuth;
